import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdTaskStatus,
  CreateCmmdBoardTaskParams,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import { CmmdApiError } from "~/environments/cmmdApi";
import { flattenTaskTree, type ProjectTaskBoardMove } from "./taskViewTypes";
import { assignmentPatch } from "./projectTaskAssignmentPatch";
import type { AssignmentOperation } from "./projectTaskAssignmentSelection";
import {
  api,
  assignCreatedTask,
  assignmentFingerprint,
  clearPendingAssignmentIfCurrent,
  errorMessage,
  hasValidMoveNeighbors,
  INITIAL,
  optimisticMovePatch,
  optimisticWorkflowPatch,
  positionMove,
  readBackAssignment,
  taskBoardReducer,
  executeBoardMove,
  type PendingAssignment,
} from "./useProjectTaskBoard.logic";

/** Coalesce the post-mutation board reloads so rapid status toggles don't fan
 * out N full GETs (P2a). The reload stays full because move/archive can cascade
 * to other tasks' blockedByCount / order / children. */
const RECONCILE_DEBOUNCE_MS = 250;

/**
 * Project-scoped CMMD task board data hook. Owns the board's fetch + optimistic
 * mutation lifecycle. `members` is provided by the caller (CmmdProjectHome,
 * which already fetches them) so they are never double-fetched, and so an
 * assignee change can optimistically resolve the denormalized name/avatar.
 */
export function useProjectTaskBoard(
  projectId: string,
  members: readonly CmmdMember[],
  agentMembers: readonly CmmdProjectAgentMember[] = [],
) {
  const [state, dispatch] = useReducer(taskBoardReducer, INITIAL);

  // Latest tasks for rollback snapshots + neighbor math, without churning
  // callback identities every render.
  const tasksRef = useRef(state.tasks);
  tasksRef.current = state.tasks;
  const membersRef = useRef(members);
  membersRef.current = members;
  const agentMembersRef = useRef(agentMembers);
  agentMembersRef.current = agentMembers;
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;
  const pendingAssignmentRef = useRef(new Map<number, PendingAssignment>());

  const reconcileTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
    },
    [],
  );
  useEffect(() => pendingAssignmentRef.current.clear(), [projectId]);

  const scheduleReconcile = useCallback(() => {
    if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
    reconcileTimer.current = setTimeout(() => {
      reconcileTimer.current = null;
      void api
        .listProjectBoard(projectId)
        .then((tasks) => {
          if (currentProjectIdRef.current === projectId) dispatch({ type: "reconcile", tasks });
        })
        .catch(() => {
          // Keep the optimistic state; a later mutation or manual reload recovers.
        });
    }, RECONCILE_DEBOUNCE_MS);
  }, [projectId]);

  const reload = useCallback(async () => {
    dispatch({ type: "load-start" });
    try {
      const tasksPromise = api.listProjectBoard(projectId);
      const workflowPromise = api
        .getProjectTaskWorkflow(projectId)
        .then((workflow) => ({ workflow, mutationBlocked: false, warning: null }) as const)
        .catch((cause) => ({
          workflow: null,
          mutationBlocked: true,
          warning:
            cause instanceof CmmdApiError && cause.kind === "permission"
              ? null
              : errorMessage(cause, "Task workflow is unavailable."),
        }));
      const [tasks, workflowResult] = await Promise.all([tasksPromise, workflowPromise]);
      dispatch({
        type: "load-ok",
        tasks,
        workflow: workflowResult.workflow,
        workflowMutationBlocked: workflowResult.mutationBlocked,
        workflowWarning: workflowResult.warning,
      });
    } catch (cause) {
      dispatch({ type: "load-error", message: errorMessage(cause, "Could not load tasks.") });
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const setStatus = useCallback(
    async (id: number, status: CmmdTaskStatus, workflowStageId?: string) => {
      if (state.workflowMutationBlocked) {
        dispatch({
          type: "mutation-error",
          message: "Task workflow is unavailable. Status changes are read-only.",
        });
        return;
      }
      const snapshot = tasksRef.current;
      const movingTask = snapshot.find((task) => task.id === id);
      dispatch({
        type: "optimistic",
        id,
        patch: {
          status,
          ...optimisticWorkflowPatch(movingTask, {
            toStatus: status,
            ...(workflowStageId ? { toStageId: workflowStageId } : {}),
          }),
        },
      });
      try {
        await api.updateProjectBoardTaskStatus(projectId, id, status, workflowStageId);
        scheduleReconcile();
      } catch (cause) {
        dispatch({
          type: "rollback",
          snapshot,
          id,
          message: errorMessage(cause, "Could not move task."),
        });
      }
    },
    [projectId, scheduleReconcile, state.workflowMutationBlocked],
  );

  const moveTask = useCallback(
    async (id: number, boardMove: ProjectTaskBoardMove) => {
      if (state.workflowMutationBlocked) {
        dispatch({
          type: "mutation-error",
          message: "Task workflow is unavailable. Board moves are read-only.",
        });
        return;
      }
      const tasks = tasksRef.current;
      const position = positionMove(boardMove);
      // Guard (F2/Codex): a reorder may only reference tasks in THIS project's list.
      if (!hasValidMoveNeighbors(tasks, position)) return;
      const snapshot = tasks;
      dispatch({
        type: "optimistic",
        id,
        patch: optimisticMovePatch(tasks, boardMove, id),
      });
      const outcomes = await Promise.allSettled(executeBoardMove(projectId, id, boardMove));
      const failure = outcomes.find(
        (outcome): outcome is PromiseRejectedResult => outcome.status === "rejected",
      );
      if (failure === undefined) {
        scheduleReconcile();
        return;
      }

      // A cross-lane move can partially succeed server-side. Reconcile immediately
      // instead of restoring a stale snapshot that contradicts the accepted half.
      try {
        const authoritativeTasks = await api.listProjectBoard(projectId);
        dispatch({ type: "reconcile", tasks: authoritativeTasks });
        dispatch({
          type: "mutation-error",
          message: errorMessage(failure.reason, "Could not fully move task."),
        });
      } catch {
        dispatch({
          type: "rollback",
          snapshot,
          id,
          message: errorMessage(failure.reason, "Could not fully move task."),
        });
      }
    },
    [projectId, scheduleReconcile, state.workflowMutationBlocked],
  );

  const updateTask = useCallback(
    async (id: number, patch: UpdateCmmdBoardTaskParams) => {
      const snapshot = tasksRef.current;
      // F7: when the assignee changes, optimistically resolve the denormalized
      // name/avatar from members so the UI doesn't show a stale assignee.
      let optimistic: Partial<CmmdBoardTask> = { ...patch } as Partial<CmmdBoardTask>;
      if (patch.assignee !== undefined) {
        const m = membersRef.current.find((x) => Number(x.id) === patch.assignee);
        optimistic = {
          ...optimistic,
          assignee: patch.assignee,
          assigneeName: m?.name ?? null,
          assigneeAvatar: m?.avatarUrl ?? null,
        };
      }
      dispatch({ type: "optimistic", id, patch: optimistic });
      try {
        await api.updateProjectBoardTask(projectId, id, patch);
        scheduleReconcile();
        return true;
      } catch (cause) {
        dispatch({
          type: "rollback",
          snapshot,
          id,
          message: errorMessage(cause, "Could not update task."),
        });
        return false;
      }
    },
    [projectId, scheduleReconcile],
  );

  const setAssignment = useCallback(
    async (
      id: number,
      subject: CmmdProjectTaskAssignmentSubject,
      // Defaults to `replace` so every existing single-subject caller keeps its
      // semantics; the multi-select sidebar passes `add` / `remove` explicitly.
      operation: AssignmentOperation = "replace",
    ) => {
      const snapshot = tasksRef.current;
      // The operation is part of the intent: an add and a remove of the same
      // subject must not share a key, or the server replays the add and the
      // remove is swallowed.
      const fingerprint = assignmentFingerprint(subject, operation);
      const pending = pendingAssignmentRef.current.get(id);
      const idempotencyKey =
        pending?.fingerprint === fingerprint ? pending.idempotencyKey : crypto.randomUUID();
      pendingAssignmentRef.current.set(id, { fingerprint, idempotencyKey });
      dispatch({
        type: "optimistic",
        id,
        patch: assignmentPatch({
          task: flattenTaskTree(tasksRef.current).find((task) => task.id === id),
          subject,
          operation,
          members: membersRef.current,
          agentMembers: agentMembersRef.current,
        }),
      });
      try {
        await api.setProjectTaskAssignment(projectId, id, {
          idempotencyKey,
          subject,
          operation,
        });
        pendingAssignmentRef.current.delete(id);
        dispatch({ type: "settle", id });
        scheduleReconcile();
        return true;
      } catch (cause) {
        try {
          const { canonical, confirmed } = await readBackAssignment(
            projectId,
            id,
            subject,
            operation,
          );
          if (currentProjectIdRef.current !== projectId) return false;
          dispatch({ type: "reconcile", tasks: canonical });
          if (confirmed) {
            clearPendingAssignmentIfCurrent(
              pendingAssignmentRef.current,
              id,
              fingerprint,
              idempotencyKey,
            );
            return true;
          }
          dispatch({
            type: "mutation-error",
            message: errorMessage(cause, "Could not assign task."),
          });
        } catch {
          if (currentProjectIdRef.current !== projectId) return false;
          dispatch({
            type: "rollback",
            snapshot,
            id,
            message: errorMessage(cause, "Could not assign task."),
          });
        }
        return false;
      }
    },
    [projectId, scheduleReconcile],
  );

  const createTask = useCallback(
    async (params: CreateCmmdBoardTaskParams, assignment?: CmmdProjectTaskAssignmentSubject) => {
      if (state.workflowMutationBlocked) {
        dispatch({
          type: "mutation-error",
          message: "Task workflow is unavailable. Task creation is read-only.",
        });
        return false;
      }
      dispatch({ type: "creating", value: true });
      try {
        const created = await api.createProjectBoardTask(projectId, params);
        if (assignment !== undefined) {
          await assignCreatedTask({
            projectId,
            createdTaskId: created.id,
            assignment,
            currentProjectIdRef,
            pendingAssignments: pendingAssignmentRef.current,
            dispatch,
          });
        }
        scheduleReconcile();
        return true;
      } catch (cause) {
        dispatch({
          type: "mutation-error",
          message: errorMessage(cause, "Could not create task."),
        });
        return false;
      } finally {
        dispatch({ type: "creating", value: false });
      }
    },
    [projectId, scheduleReconcile, state.workflowMutationBlocked],
  );

  const archive = useCallback(
    async (id: number) => {
      const snapshot = tasksRef.current;
      dispatch({ type: "optimistic", id, patch: { archived: true } });
      try {
        await api.archiveProjectBoardTask(projectId, id);
        scheduleReconcile();
        return true;
      } catch (cause) {
        dispatch({
          type: "rollback",
          snapshot,
          id,
          message: errorMessage(cause, "Could not archive task."),
        });
        return false;
      }
    },
    [projectId, scheduleReconcile],
  );

  const unarchive = useCallback(
    async (id: number) => {
      const snapshot = tasksRef.current;
      dispatch({ type: "optimistic", id, patch: { archived: false } });
      try {
        await api.unarchiveProjectBoardTask(projectId, id);
        scheduleReconcile();
      } catch (cause) {
        dispatch({
          type: "rollback",
          snapshot,
          id,
          message: errorMessage(cause, "Could not unarchive task."),
        });
      }
    },
    [projectId, scheduleReconcile],
  );

  return useMemo(
    () => ({
      ...state,
      reload,
      setStatus,
      moveTask,
      updateTask,
      setAssignment,
      createTask,
      archive,
      unarchive,
    }),
    [state, reload, setStatus, moveTask, updateTask, setAssignment, createTask, archive, unarchive],
  );
}

export type ProjectTaskBoard = ReturnType<typeof useProjectTaskBoard>;
